/**
 * The ERP / master-data import FLOW, and the per-stage controls the agent may
 * point at but never press.
 *
 * Deliberately not entries in the shared `agentTargets` catalog, for the same
 * reason `customerStudioAgentTargets` and `declarationReviewAgentTargets` are
 * not: that catalog keys a target to ONE FIXED route, and neither half of this
 * flow has one. The route carries the staged files in `?file_ids=`, and which
 * stage is on screen — a file's column mapping, or Review & Commit — is
 * `MasterDataMappingModule`'s own state, addressed through `?step=`.
 *
 * A catalog entry could only name the bare path `/master-data/mapping`, or (as
 * `import.commit-button` used to) the UPLOAD page, which contains neither
 * control. That entry was worse than missing: `resolveTargetKeysForRoute`
 * matches by exact route, so standing on the upload screen reported the Commit
 * button as already visible, the agent said it had highlighted it, and the
 * pulse then timed out against an element that was never there.
 *
 * Each control is marked in the DOM with `data-agent-target="<key>"`, the one
 * convention `targetSelector` reads.
 */
import {
  targetSelector,
  type AgentTarget,
} from "@/features/agent-chat/actions/agentTargets"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"

/** Where the import flow's stages live. `import.master-data` is the upload
 *  screen; this is the mapping/review wizard the files move through. */
export const MASTER_DATA_MAPPING_ROUTE =
  "/material-compliance/master-data/mapping"

/**
 * The Review & Commit stage. Every other step value is a staged file's own id —
 * `MasterDataMappingModule` keys its step cards by file id, so the URL and the
 * component already speak the same vocabulary.
 */
export const IMPORT_REVIEW_STEP = "review"

/**
 * Build the flow's deeplink. `file_ids` repeats per file, matching what the
 * upload handler and the history row already navigate with; `step` names the
 * stage to open at.
 */
export function masterDataImportStepRoute(
  fileIds: string[],
  step?: string,
): string {
  const params = new URLSearchParams(fileIds.map((id) => ["file_ids", id]))
  if (step) params.append("step", step)
  const query = params.toString()
  return query
    ? `${MASTER_DATA_MAPPING_ROUTE}?${query}`
    : MASTER_DATA_MAPPING_ROUTE
}

/**
 * Is the user already looking at the stage this hand-over addresses?
 *
 * Answered from the entities `MasterDataMappingModule` registers, NOT from the
 * URL: a user who reached the flow by clicking a history row has no `?step=` at
 * all, so comparing routes would call that "somewhere else" and offer a
 * "Take me there" button for the screen already in front of them. The backend's
 * `open_mc_import` reads the same three entities to decide the same thing, so
 * the wording and the affordance cannot disagree.
 *
 * Exact file-set equality: opening a subset rewrites the flow's file list, so a
 * partial match is a real navigation, not a no-op.
 */
export function isAtImportStage(
  entities: Record<string, unknown> | undefined,
  fileIds: string[],
  step: string,
): boolean {
  if (!entities || entities.mc_entity !== "import_mapping") return false
  if (entities.import_active_step !== step) return false
  const onScreen = entities.import_file_ids
  if (!Array.isArray(onScreen)) return false
  const here = new Set(onScreen.map(String))
  return (
    here.size === new Set(fileIds).size && fileIds.every((id) => here.has(id))
  )
}

// ── The two controls that move an import forward ─────────────────────────────

export const IMPORT_CONTROLS = ["confirm_mapping", "commit"] as const

export type ImportControl = (typeof IMPORT_CONTROLS)[number]

const IMPORT_CONTROL_KEYS: Record<
  ImportControl,
  { key: string; labelKey: TranslationKeys }
> = {
  confirm_mapping: {
    key: "import.confirm-mapping-button",
    labelKey: "AI_CHAT/TARGET_LABEL_IMPORT_CONFIRM_MAPPING",
  },
  commit: {
    key: "import.commit-button",
    labelKey: "AI_CHAT/TARGET_LABEL_IMPORT_COMMIT",
  },
}

export function isImportControl(value: unknown): value is ImportControl {
  return (
    typeof value === "string" &&
    (IMPORT_CONTROLS as readonly string[]).includes(value)
  )
}

/**
 * Neither control is in the DOM on arrival: the module renders a skeleton until
 * `useAnalyzedFiles` resolves, and only then mounts the stage that holds the
 * button. The default wait expires first and would report the control as
 * missing when it was merely not there yet.
 */
const CONTROL_WAIT_MS = 10_000

export function importControlTarget(
  control: ImportControl,
  route: string,
): AgentTarget {
  const descriptor = IMPORT_CONTROL_KEYS[control]
  return {
    route,
    selector: targetSelector(descriptor.key),
    labelKey: descriptor.labelKey,
    waitTimeoutMs: CONTROL_WAIT_MS,
  }
}
